import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, Inject, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { WwniSubCategoryService } from '../services/wwni-sub-category.service';

@Component({
    selector: 'gtw-wwni-sub-category',
    templateUrl: './wwni-sub-category.component.html',
    styleUrls: ['./wwni-sub-category.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WwniSubCategoryComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;

  // Component specific properties
  displaySubcategory: any[] = [];
  crudLoading = false;
  noRecords = false;
  modalTitle = "WWNI Sub Category";

  // Sorting properties
  sort_key: { [key: string]: number } = {
    '1': 0, '2': 0, '3': 0, '4': 0, '5': 0, '6': 0, '7': 0
  };
  orderByField = '';

  // Input properties for data
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  constructor(
    @Inject(WwniSubCategoryService) private wwniSubCategoryService: WwniSubCategoryService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.getSubCategory();
      }
    } catch (e) {
      console.error('Error in WWNI Sub Category init:', e);
    }
  }

  getSubCategory() {
    this.crudLoading = true;
    this.noRecords = false;

    const gp = this.globalService?.globalParams || {};

    const params = {
      "action_code": 'm4hj9g',
      "tax_year": this.filterParams.tax_year || gp.tax_year,
      "scenario": this.filterParams.scenario || gp.scenario,
      "jcd_key": this.filterParams.jcd_key || gp.jcd_key,
      'source_combination_key': this.rowData.SOURCE_COMBINATION_KEY,
      'target_combination_key': this.rowData.COMPL_COMBINATION_KEY,
      'catg_acct_key': 0,
      'me_key': this.rowData.SOURCE_ME_KEY
    };

    this.wwniSubCategoryService.getSubCategoryData(
      this.GENERAL_CONFIG.base_url,
      params,
      gp,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        
        if (response.errorMessage && response.errorMessage.length > 0 && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          return;
        }

        this.displaySubcategory = response.jsonObject || [];
        if (this.displaySubcategory.length === 0) {
          this.noRecords = true;
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error occurred while loading data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        console.error('Error loading sub category data:', error);
        this.cdr.markForCheck();
      }
    );
  }

  sortBy(columnKey: string, currentSortState: number) {
    // Reset all other sort keys
    Object.keys(this.sort_key).forEach(key => {
      if (key !== columnKey) {
        this.sort_key[key] = 0;
      }
    });

    // Cycle through sort states: 0 -> 1 -> 2 -> 0
    this.sort_key[columnKey] = (currentSortState + 1) % 3;

    // Apply sorting based on the column and sort direction
    const sortDirection = this.sort_key[columnKey];
    if (sortDirection === 0) {
      // No sorting - restore original order
      this.getSubCategory();
      return;
    }

    const fieldMap: { [key: string]: string } = {
      '1': 'LEID',
      '2': 'CDR_NO',
      '3': 'REPORTING_PERIOD',
      '4': 'CURR',
      '5': 'ME_CODE',
      '6': 'ACCT_DESC',
      '7': 'SUB_CATG_AMOUNT'
    };

    const field = fieldMap[columnKey];
    if (field) {
      this.displaySubcategory.sort((a, b) => {
        let aVal = a[field] || '';
        let bVal = b[field] || '';

        // Handle numeric sorting for amount
        if (field === 'SUB_CATG_AMOUNT') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else {
          aVal = aVal.toString().toLowerCase();
          bVal = bVal.toString().toLowerCase();
        }

        if (sortDirection === 1) { // Ascending
          return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        } else { // Descending
          return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
        }
      });
    }
    this.cdr.markForCheck();
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}
